import type { IconButtonProps } from "@ids/react-icon-button";
import type { LoaderType } from "@ids/react-loader";
import {
  type ButtonIconAlignment,
  type ButtonSize,
  type ButtonVariant,
  ButtonType as IdsButtonType,
} from "@ids/web-component-button";
import type {
  FunctionComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
} from "react";

import type { ValueOf } from "@/utils/valueOf";

export type IdsButtonProps = HTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  type?: ButtonType;
  htmlType?: "button" | "submit" | "reset";
  size?: ButtonSize;
  iconAlignment?: ButtonIconAlignment;
  inverted?: boolean;
  hovered?: boolean;
  focused?: boolean;
  active?: boolean;
  disabled?: boolean;
};

// --- Higher level Button component -----------------------------------------
export type LoadingProps = {
  loading?: boolean;
  loaderType?: LoaderType;
  children?: ReactNode;
};

export type ButtonProps = IdsButtonProps & LoadingProps;

// Variant helper type omitting "type" so we can enforce each variant's style
export type VariantButtonProps = Omit<ButtonProps, "type">;

// Build the exported Button object with variant shortcuts
export interface ButtonComponent extends FunctionComponent<ButtonProps> {
  Primary: FunctionComponent<VariantButtonProps>;
  Secondary: FunctionComponent<VariantButtonProps>;
  Tertiary: FunctionComponent<VariantButtonProps>;
  Text: FunctionComponent<VariantButtonProps>;
  Icon: FunctionComponent<IconButtonProps & RefAttributes<HTMLButtonElement>>;
}

/**
 * Combines all properties from `ButtonStyle` and `ButtonType` into a single constant object.
 * This object can be used to access all button style and type options in one place.
 *
 * @remarks
 * The combined object is marked as `const` to ensure its properties are readonly.
 *
 * @see ButtonStyle
 * @see IdsButtonType
 */
export const ButtonType = {
  PRIMARY: "primary", //Filled
  SECONDARY: "secondary", //Outlined
  TERTIARY: "tertiary", //Tonal
  ...IdsButtonType,
} as const;
export type ButtonType = ValueOf<typeof ButtonType>;
